// src/data/rankLadder.ts
// The career ladder's ranks (RANKS.md "Promotion inside a tier", built in 55.90): which rank each rung gives on entry,
// and at which player level it promotes, up to the rung's ceiling. One table for the browser — getCorrectRank
// (utils/rankUtils.ts) derives from it and the department page's Karjäär tab draws it — mirroring deriveRank in
// functions/src/rank.ts, the authority; tests/unit/rank-ladder.test.mjs checks the two agree at every level.
//
// No imports on purpose: the unit test loads this file directly with Node.

export type LadderTierId = 'abipolitseinik' | 'kadett' | 'officer' | 'group' | 'lead' | 'station' | 'prefect';

export interface LadderStep {
    rank: string;
    /** The player level it is reached at; 0 on entering the rung. */
    from: number;
}

export interface LadderTier {
    /** RANKS.md's tier number. */
    tier: number;
    id: LadderTierId;
    title: string;
    /** Lowest first; empty for a rung without a rank (abipolitseinik). */
    steps: LadderStep[];
}

export const RANK_LADDER: LadderTier[] = [
    { tier: 1, id: 'abipolitseinik', title: 'Abipolitseinik', steps: [] },
    { tier: 2, id: 'kadett', title: 'Kadett', steps: [{ rank: 'nooreminspektor', from: 0 }] },
    {
        tier: 3, id: 'officer', title: 'Talituse ametnik', steps: [
            { rank: 'inspektor', from: 0 }, { rank: 'vaneminspektor', from: 40 },
            { rank: 'üleminspektor', from: 60 }, { rank: 'komissar', from: 80 },
        ],
    },
    {
        tier: 4, id: 'group', title: 'Grupijuht', steps: [
            { rank: 'vanemkomissar', from: 0 }, { rank: 'politseileitnant', from: 80 }, { rank: 'politseikapten', from: 100 },
        ],
    },
    {
        tier: 5, id: 'lead', title: 'Talituse juht', steps: [
            { rank: 'politseileitnant', from: 0 }, { rank: 'politseikapten', from: 100 }, { rank: 'politseimajor', from: 120 },
        ],
    },
    {
        tier: 6, id: 'station', title: 'Jaoskonnajuht', steps: [
            { rank: 'politseikapten', from: 0 }, { rank: 'politseimajor', from: 150 }, { rank: 'politseikolonelleitnant', from: 170 },
        ],
    },
    {
        tier: 7, id: 'prefect', title: 'Prefekt', steps: [
            { rank: 'politseimajor', from: 0 }, { rank: 'politseikolonelleitnant', from: 170 }, { rank: 'politseikolonel', from: 190 },
            { rank: 'politseikindralinspektor', from: 220 }, { rank: 'politseikindral', from: 250 },
        ],
    },
];

/** The rung a position stands on. */
export function tierOfPosition(position: string | null | undefined): LadderTierId | null {
    if (!position) return null;
    if (position === 'abipolitseinik' || position === 'kadett') return position;
    if (position === 'prefekt') return 'prefect';
    if (position === 'jaoskonnajuht') return 'station';
    if (position.startsWith('talituse_juht')) return 'lead';
    if (position.startsWith('grupijuht')) return 'group';
    return 'officer';
}

/** The rank a rung gives at a level: the highest step reached. */
export function rankOnLadder(tier: LadderTierId, level: number): string | null {
    const steps = RANK_LADDER.find((t) => t.id === tier)?.steps ?? [];
    let rank: string | null = null;
    for (const step of steps) if (level >= step.from) rank = step.rank;
    return rank;
}
